import { createContext, useContext, useState, useRef, ReactNode, useEffect, useSyncExternalStore } from 'react';
import { NotificationService, UserService, YapService } from '../services/api';
import { bootMark } from '../debug/bootTracer';
import { Notification } from '../types';
import { auth } from '../services/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { DISABLE_ASYNC_EFFECTS } from '../debugConfig';
import { useUserData } from './UserDataContext';
import { useTheme } from './ThemeProvider';
import { toast } from 'sonner';
import { UserPlus, Heart, MessageSquare, Repeat2, Sparkles, Bell } from 'lucide-react';
import { EVENTS } from '../constants/navigation';
import { calculateAge } from './utils/timeFormat';
import { getUserAvatar } from '../utils/userUtils';

// ─────────────────────────────────────────────────────────────────────────────
// SINGLETON EXTERNAL STORE
// Lives entirely outside of React. Updating it never causes a React render
// unless a subscribed hook explicitly computes a new snapshot value.
// ─────────────────────────────────────────────────────────────────────────────
interface NotificationStore {
  notifications: Notification[];
  /** False until the first snapshot lands, so an empty list can be told from an unloaded one. */
  loaded: boolean;
  subscribers: Set<() => void>;
}

const notificationStore: NotificationStore = {
  notifications: [],
  loaded: false,
  subscribers: new Set(),
};

/** Push a new list into the store and notify all subscribers. */
function updateStore(next: Notification[]) {
  notificationStore.notifications = next;
  notificationStore.loaded = true;
  console.log('[NOTIF] Store updated. Badge count:', next.filter(n => !n.read).length);
  notificationStore.subscribers.forEach(fn => fn());
}

/** useSyncExternalStore subscribe callback — stable reference per mount. */
function storeSubscribe(onStoreChange: () => void) {
  notificationStore.subscribers.add(onStoreChange);
  return () => {
    notificationStore.subscribers.delete(onStoreChange);
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT — only holds lightweight "badge" primitives
// ─────────────────────────────────────────────────────────────────────────────
interface BadgeState {
  unreadCount: number;
  incomingRequestCount: number;
}

interface NotificationContextType {
  badges: BadgeState;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  getPendingFriendRequestCount: () => number;
  isFriendRequestPending: (userId: string) => boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER — drives the Firestore listener + badge state
// ─────────────────────────────────────────────────────────────────────────────
export function NotificationProvider({ children }: { children: ReactNode }) {
  const { userProfile } = useUserData();
  const { theme } = useTheme();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [badges, setBadges] = useState<BadgeState>({ unreadCount: 0, incomingRequestCount: 0 });
  const pendingFriendRequestsRef = useRef<Set<string>>(new Set());

  // Stable ref so snapshot callbacks never stale-close over these values
  const lastNotifiedIdRef = useRef<string | null>(null);
  const userProfileRef = useRef(userProfile);
  const currentUserRef = useRef(currentUser);
  const themeRef = useRef(theme);
  useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
  useEffect(() => { themeRef.current = theme; }, [theme]);

  // ── Auth listener ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (DISABLE_ASYNC_EFFECTS) {
      console.log('ASYNC EFFECT SKIPPED: Notif.AuthState');
      window.dispatchEvent(new CustomEvent('SKIPPED_EFFECT', { detail: 'Notif.AuthState' }));
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    const setupAuth = async () => {
      try {
        bootMark('NotificationInitStart');
        const { getAuthSafe } = await import('../services/firebaseConfig');
        const authInstance = await getAuthSafe();
        if (cancelled) return;

        unsubscribe = onAuthStateChanged(authInstance, (user) => {
          if (cancelled) return;
          setCurrentUser(user);
          console.log(`[NotificationProvider] Auth state changed. User: ${user ? user.uid : 'null'}`);
          bootMark('NotificationInitDone');
        });
      } catch (err) {
        console.error('[NotificationProvider] Auth init failed', err);
      }
    };

    setupAuth();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // ── Firestore notification listener ───────────────────────────────────────
  useEffect(() => {
    if (DISABLE_ASYNC_EFFECTS) {
      console.log('ASYNC EFFECT SKIPPED: Notif.Subscribe');
      window.dispatchEvent(new CustomEvent('SKIPPED_EFFECT', { detail: 'Notif.Subscribe' }));
      return;
    }

    if (!currentUser || !userProfile?.id) {
      // User signed out — wipe the store
      updateStore([]);
      pendingFriendRequestsRef.current = new Set();
      setBadges({ unreadCount: 0, incomingRequestCount: 0 });
      return;
    }

    console.log(`[NotificationProvider] Subscribing for uid: ${userProfile.id}`);

    // ── Async safety: use a ref slot so the cleanup can always call unsub
    let activeUnsub: (() => void) | null = null;
    let dismounted = false;
    let isFirstCallback = true;

    const setupSubscription = async () => {
      const userIds = [...new Set([String(userProfile.id), ...(userProfile.originalId ? [String(userProfile.originalId)] : [])])];

      const unsub = await NotificationService.subscribeToNotifications(userIds, (fetchedNotifications) => {
        if (dismounted) return;

        // ── Initial load: suppress stale toasts ───────────────────────────
        if (isFirstCallback) {
          console.log(`[NotificationProvider] Initial snapshot: ${fetchedNotifications.length} items. Suppressing historical toasts.`);
          if (fetchedNotifications.length > 0) {
            lastNotifiedIdRef.current = fetchedNotifications[0].id;
          }
          isFirstCallback = false;

          // Hydrate store silently — no badge state update yet needed
          updateStore(fetchedNotifications);
          _syncBadges(fetchedNotifications);
          return;
        }

        // ── Subsequent real-time updates ──────────────────────────────────
        console.log('[NOTIF] Full list sync triggered');

        // Push into store silently (feeds are NOT subscribed to this)
        updateStore(fetchedNotifications);
        _syncBadges(fetchedNotifications);

        // ── Toast logic (only for genuinely new, recent, non-self notifications)
        const newest = fetchedNotifications[0];
        if (newest && !newest.read && newest.id !== lastNotifiedIdRef.current) {
          const up = userProfileRef.current;
          const cu = currentUserRef.current;

          const notificationTime = (newest.timestamp && typeof (newest.timestamp as any).toDate === 'function')
            ? (newest.timestamp as any).toDate().getTime()
            : (newest.timestamp && (newest.timestamp as any).seconds)
              ? (newest.timestamp as any).seconds * 1000
              : typeof newest.timestamp === 'string' ? new Date(newest.timestamp).getTime() : Date.now();

          const fromSelf = newest.metadata?.fromUserId && (
            String(newest.metadata.fromUserId) === String(up?.id) ||
            String(newest.metadata.fromUserId) === String(up?.originalId) ||
            (cu && String(newest.metadata.fromUserId) === String(cu.uid))
          );

          const isRecent = (Date.now() - notificationTime) < 60000;

          if (isRecent && !fromSelf) {
            lastNotifiedIdRef.current = newest.id;
            _fireToast(newest, up, cu);
          } else {
            lastNotifiedIdRef.current = newest.id;
          }
        }
      });

      if (dismounted) {
        // Component unmounted before the promise resolved — kill immediately
        unsub();
      } else {
        activeUnsub = unsub;
      }
    };

    setupSubscription();

    return () => {
      dismounted = true;
      if (activeUnsub) {
        activeUnsub();
        activeUnsub = null;
      }
    };
  }, [currentUser, userProfile?.id, userProfile?.originalId]);

  // ── Internal helpers ───────────────────────────────────────────────────────

  /** Recompute badge primitives and only setState when they actually change. */
  function _syncBadges(notifs: Notification[]) {
    // Skip DM notifications — they have their own unread badge on the Messages tab.
    // Including them here would make the bell badge show a number that has no
    // corresponding item in the visible notifications list (it's filtered out).
    const DM_TYPES = new Set(['MESSAGE', 'DM', 'DIRECT_MESSAGE', 'CHAT_MESSAGE', 'NEW_MESSAGE']);
    const isDm = (t: string | undefined) => t ? DM_TYPES.has(t.toUpperCase()) : false;
    // Rooms moved to the Messages tab, so they leave this badge with the DMs.
    // Counting them here would be the very thing the comment above warns about:
    // a number on the bell with no matching item in the list it opens.
    const isRoom = (t: string | undefined) => String(t || '').toUpperCase().startsWith('ROOM_');
    const unread = notifs.filter(n => !n.read && !isDm(n.type) && !isRoom(n.type)).length;
    const requests = new Set<string>();
    notifs.forEach(n => {
      if ((n.type?.toUpperCase() === 'FRIEND_REQUEST' || n.type === 'friend_request') && !n.read) {
        const senderId = n.metadata?.fromUserId;
        if (senderId) requests.add(String(senderId));
      }
    });
    pendingFriendRequestsRef.current = requests;
    setBadges(prev => {
      const next = { unreadCount: unread, incomingRequestCount: requests.size };
      // Bail out if nothing changed — prevents any badge-triggered re-render
      if (prev.unreadCount === next.unreadCount && prev.incomingRequestCount === next.incomingRequestCount) {
        return prev;
      }
      console.log('[NOTIF] Badge state updated:', next);
      return next;
    });
  }

  /** Fire an in-app toast for a real-time notification. */
  function _fireToast(newest: Notification, up: any, cu: any) {
    let Icon = Bell;
    const type = newest.type?.toUpperCase() ?? '';

    // Chat / DM notifications already surface in the Messages tab (unread
    // count + last-message preview). Firing an additional in-app toast is
    // redundant — the user sees TWO notifications for one event. Skip it.
    const DM_TYPES = new Set(['MESSAGE', 'DM', 'DIRECT_MESSAGE', 'CHAT_MESSAGE', 'NEW_MESSAGE']);
    if (DM_TYPES.has(type)) {
      return;
    }
    // A room MESSAGE surfaces in the Messages tab exactly like a DM, so a toast
    // on top of it is the same double-notification. The other ROOM_* types are
    // one-off events that nothing else announces, so they still toast.
    if (type === 'ROOM_MESSAGE') {
      return;
    }

    // Nothing about a conversation you are ALREADY LOOKING AT.
    //
    // Being told someone spoke, on the screen where you can watch them speak,
    // is noise that also covers the message it is announcing. The check is the
    // route rather than a presence record: what matters is what is on screen
    // right now, and that is the one thing the client knows for certain.
    try {
      const here = window.location.pathname || '';
      const meta: any = newest.metadata || {};
      const roomId = String((newest as any).roomId || meta.roomId || '');
      const senderId = String((newest as any).senderId || meta.senderId || meta.fromUserId || '');
      if (roomId && here.includes(`/room/${roomId}`)) return;
      if (senderId && here.includes(`/messages/${senderId}`)) return;
    } catch { /* location unavailable */ }
    if (type === 'FRIEND_REQUEST') Icon = UserPlus;
    else if (type === 'YAP_LIKE') Icon = Heart;
    else if (type === 'YAP_COMMENT') Icon = MessageSquare;
    else if (type === 'YAP_SHOUT') Icon = Repeat2;
    // Matches the notifications list. Only a fallback now that the toast prefers
    // the sender's face, but it should not disagree with the list when it shows.
    else if (type === 'MENTION') Icon = MessageSquare;

    const handleNotificationClick = async () => {
      try {
        const meta = newest.metadata || {};

        if (type.includes('YAP') || type.includes('LIKE') || type.includes('COMMENT')) {
          const targetId = meta.yapId || meta.targetId;
          if (targetId) {
            const post = await YapService.findPostRef(targetId);
            if (post?.data) {
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: { id: post.ref.id, ...post.data } }));
              return;
            }
          }
        }

        if (type === 'YAP_SHOUT') {
          const targetId = meta.yapId || meta.shoutId;
          if (targetId) {
            const post = await YapService.findPostRef(targetId);
            if (post?.data) {
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SHOUT_DETAIL, { detail: { id: post.ref.id, ...post.data } }));
              return;
            }
          }
        }

        if (type.includes('FRIEND') || type.includes('CONNECTION') || type === 'SLAP' || type === 'slap') {
          const senderId = meta.fromUserId;
          if (senderId) {
            const user = await UserService.fetchUserById(senderId);
            if (user) {
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, { detail: user }));
              return;
            }
          }
        }

        // Rooms are short-lived, so a tap has to land while the room still
        // exists; there is nothing to fall back to once it is purged.
        if (type.startsWith('ROOM_')) {
          const roomId = meta.roomId || meta.targetId;
          if (roomId) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_ROOM, { detail: { id: String(roomId) } }));
            return;
          }
        }

        // Same routing as the notifications list: a notification tied to a
        // specific business opens it, otherwise My Business, which is where an
        // approval's Pay CTA lives.
        if (type.startsWith('BUSINESS_')) {
          const businessProfileId = (newest as any).businessProfileId || meta.businessProfileId;
          if (businessProfileId) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_BUSINESS_PROFILE, { detail: { id: String(businessProfileId) } }));
          } else {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_MY_BUSINESS));
          }
          return;
        }

        if (type === 'TRIBE_JOIN' || type === 'FORUM_JOIN') {
          const groupId = meta.tribeId || meta.forumId || meta.targetId;
          if (groupId) {
            try {
              const db = await (await import('../services/firebaseConfig')).getDbSafe();
              const { getDoc, doc } = await import('firebase/firestore');
              const groupDoc = await getDoc(doc(db, type === 'TRIBE_JOIN' ? 'tribes' : 'forums', groupId));
              if (groupDoc.exists()) {
                const event = type === 'TRIBE_JOIN' ? EVENTS.NAVIGATE_TO_TRIBE_GROUP : EVENTS.NAVIGATE_TO_HISTORY_FORUM;
                window.dispatchEvent(new CustomEvent(event as string, { detail: { id: groupDoc.id, ...groupDoc.data() } }));
                return;
              }
            } catch (err) {
              console.error('[NotificationProvider] Group fetch failed:', err);
            }
          }
        }
      } catch (err) {
        console.error('[NotificationProvider] Navigation failed:', err);
      }
    };

    const currentTheme = themeRef.current;
    const displayTitle = (newest.title || 'New Notification')
      .replace(/matched with/gi, 'are now friends with')
      .replace(/Matched!/gi, 'You are now friends!');
    const displayMessage = (newest.message || '')
      .replace(/matched with/gi, 'are now friends with');

    // The sender's face, when there is one.
    //
    // A generic glyph tells you a notification arrived; a face tells you WHO,
    // which is the thing you actually wanted to know and the thing that decides
    // whether you open it. The icon stays as the fallback for notifications
    // with no person behind them.
    const toastAvatar = String(
      (newest as any).senderAvatar
      || (newest.metadata as any)?.senderAvatar
      || (newest.metadata as any)?.fromUserAvatar
      || (newest as any).userAvatar
      || '',
    );

    toast(displayTitle, {
      description: displayMessage,
      icon: toastAvatar ? (
        // Wrapped in a fixed box rather than styling the <img> directly.
        //
        // The toast lays its icon out in a flex row, where an image with a width
        // is still free to SHRINK — the box narrowed while its height stayed,
        // which is what read as the avatar being stretched vertically.
        // flexShrink: 0 plus explicit min sizes stop that, and the image fills a
        // container that can no longer change shape.
        <span
          style={{
            width: 28,
            height: 28,
            minWidth: 28,
            minHeight: 28,
            flexShrink: 0,
            display: 'block',
            borderRadius: '50%',
            overflow: 'hidden',
            border: `1px solid ${currentTheme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
          }}
        >
          <img
            src={toastAvatar}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </span>
      ) : (
        <Icon className="w-4 h-4" style={{ color: currentTheme === 'dark' ? '#3498DB' : '#F39C12' }} />
      ),
      // Sonner sizes the icon slot at 16px and the 28px avatar overflows it into
      // the title. Sonner's own margin variable is what spaces the text, so widen
      // that (12px of overflow plus a 10px gap) rather than restyling the avatar.
      ...(toastAvatar ? { style: { ['--toast-icon-margin-end' as any]: '22px' } } : {}),
      duration: 5000,
      action: {
        label: 'View',
        onClick: (e) => { e.preventDefault(); e.stopPropagation(); handleNotificationClick(); },
      },
      onClick: () => handleNotificationClick(),
    });

    // 🤝 Passive match celebration
    if (newest.type?.toUpperCase() === 'FRIEND_ACCEPTED' && newest.metadata?.fromUserId) {
      const senderId = String(newest.metadata.fromUserId);
      (async () => {
        try {
          const senderProfile = await UserService.fetchUserById(senderId);
          if (senderProfile) {
            window.dispatchEvent(new CustomEvent(EVENTS.SHOW_MATCH_POPUP, {
              detail: {
                ...senderProfile,
                image: getUserAvatar(senderProfile),
                age: calculateAge(senderProfile.dateOfBirth) ?? undefined,
                location: senderProfile.home_residence || senderProfile.location || 'Earth',
              },
            }));
          }
        } catch (err) {
          console.warn('[NotificationProvider] Failed to fetch profile for passive match celebration:', err);
        }
      })();
    }

    // 🥊 Real-time slap event
    if (type === 'SLAP' || type === 'slap') {
      const senderId = newest.metadata?.fromUserId;
      if (senderId) {
        window.dispatchEvent(new CustomEvent(EVENTS.REAL_TIME_SLAP, {
          detail: { fromUserId: String(senderId), slapsBurst: newest.metadata?.slapsBurst || 1 },
        }));
      }
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  const addNotification = async (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const recipientId = notification.userId
      ? String(notification.userId)
      : (userProfile?.id ? String(userProfile.id) : (currentUser ? currentUser.uid : null));

    if (recipientId) {
      await NotificationService.sendNotification(
        recipientId,
        notification.type,
        notification.title,
        notification.message,
        notification.metadata,
      );
    }
  };

  const markAsRead = async (notificationId: string) => {
    // Optimistic update in the external store
    const next = notificationStore.notifications.map(n => n.id === notificationId ? { ...n, read: true } : n);
    updateStore(next);
    _syncBadges(next);
    await NotificationService.markAsRead(notificationId);
  };

  const markAllAsRead = async () => {
    const before = notificationStore.notifications;

    // Optimistic, but reversible. The write used to swallow its own failures, so
    // a rejected batch left this "all read" on screen until the listener
    // replaced it with the truth a moment later — which is exactly what "it
    // marks them read and then they come back" looked like.
    const next = before.map(n => ({ ...n, read: true }));
    updateStore(next);
    _syncBadges(next);

    // Both ids: notifications addressed to a pre-merge originalId are displayed
    // and counted by the listener above, so they have to be clearable too.
    const ids = [userProfile?.id, (userProfile as any)?.originalId, auth.currentUser?.uid]
      .filter(Boolean)
      .map(id => String(id));
    if (ids.length === 0) return;

    try {
      await NotificationService.markAllAsRead([...new Set(ids)]);
    } catch (e) {
      console.error('[NOTIF] markAllAsRead failed; rolling back', e);
      updateStore(before);
      _syncBadges(before);
      const { toast } = await import('sonner');
      toast.error('Could not mark everything as read');
    }
  };

  const clearNotification = (notificationId: string) => {
    const next = notificationStore.notifications.filter(n => n.id !== notificationId);
    updateStore(next);
    _syncBadges(next);
  };

  const clearAllNotifications = () => {
    updateStore([]);
    pendingFriendRequestsRef.current = new Set();
    setBadges({ unreadCount: 0, incomingRequestCount: 0 });
  };

  const getPendingFriendRequestCount = () => pendingFriendRequestsRef.current.size;

  return (
    <NotificationContext.Provider
      value={{
        badges,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAllNotifications,
        getPendingFriendRequestCount,
        isFriendRequestPending: (userId: string) => pendingFriendRequestsRef.current.has(userId),
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC HOOKS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * useNotifications — Full list hook.
 * Use ONLY in NotificationsScreen. Re-renders on every store update (that's
 * intentional — this screen needs to stay live).
 */
export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');

  // useSyncExternalStore: React 18 concurrent-safe subscription to the store
  const notifications = useSyncExternalStore(
    storeSubscribe,
    () => notificationStore.notifications,
    () => notificationStore.notifications, // server snapshot (SSR safety)
  );

  const notificationsLoaded = useSyncExternalStore(
    storeSubscribe,
    () => notificationStore.loaded,
    () => notificationStore.loaded,
  );

  return {
    notifications,
    notificationsLoaded,
    unreadCount: ctx.badges.unreadCount,
    incomingRequestCount: ctx.badges.incomingRequestCount,
    addNotification: ctx.addNotification,
    markAsRead: ctx.markAsRead,
    markAllAsRead: ctx.markAllAsRead,
    clearNotification: ctx.clearNotification,
    clearAllNotifications: ctx.clearAllNotifications,
    getPendingFriendRequestCount: ctx.getPendingFriendRequestCount,
    isFriendRequestPending: ctx.isFriendRequestPending,
  };
}

/**
 * useNotificationBadges — Lightweight badge hook.
 * Use in DiscoverScreen, YapTabScreen, BottomNav, ChatScreen, etc.
 * ONLY re-renders when unreadCount or incomingRequestCount actually changes.
 * Guaranteed zero re-renders from raw Firestore document updates.
 */
export function useNotificationBadges() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotificationBadges must be used within a NotificationProvider');
  return {
    unreadCount: ctx.badges.unreadCount,
    incomingRequestCount: ctx.badges.incomingRequestCount,
    addNotification: ctx.addNotification,
    isFriendRequestPending: ctx.isFriendRequestPending,
  };
}
